import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { FIX_FAILED } from './fixes';

import type { SceneWarning } from '@platform/schema/scene-fix';

/**
 * The rules one field breaks, beside the field, with Fix with AI where a rule allows it.
 *
 * Green once a fix leaves nothing behind, saying what the text now measures. Revert is
 * offered while the field still holds what the fix wrote.
 */
export function FieldWarnings({
  field,
  warnings,
  predatesRules = false,
  fixed,
  byHand = 'Edit the field above by hand.',
  fixing = false,
  failed = false,
  disabled = false,
  onFix,
  onRevert,
}: {
  field: string;
  warnings: readonly SceneWarning[];
  /** A subheadline written before the rules: they warn and it stays live. */
  predatesRules?: boolean;
  /** What the text measures after a fix, while Revert is still possible. */
  fixed?: string | undefined;
  /** What to do when AI may not, where "the field above" is not one field. */
  byHand?: string;
  /** Left out where the rules are read-only, as no Fix with AI can clear them. */
  fixing?: boolean;
  failed?: boolean;
  disabled?: boolean;
  onFix?: () => void;
  onRevert?: () => void;
}) {
  if (warnings.length === 0 && fixed === undefined) return null;

  const fixable = warnings.some((one) => one.fixable);
  const blocks = warnings.some((one) => one.blocks);

  const severity =
    warnings.length === 0 ? 'success' : blocks ? 'error' : 'warning';

  const heading =
    warnings.length === 0
      ? `Fixed. ${fixed ?? ''}`
      : blocks
        ? 'Saved as a draft. The lesson cannot be published until this is fixed.'
        : predatesRules
          ? 'Needs review. This subheadline predates the rules and stays live until it is edited.'
          : 'Needs review.';

  return (
    <Alert
      severity={severity}
      data-testid={`${field}-warnings`}
      action={
        <Stack direction="row" spacing={1}>
          {fixed !== undefined && onRevert !== undefined && (
            <Button
              size="small"
              color="inherit"
              disabled={disabled || fixing}
              onClick={onRevert}
            >
              Revert
            </Button>
          )}
          {fixable && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={disabled || fixing}
              onClick={onFix}
            >
              {fixing ? 'Fixing... (up to 2 min)' : 'Fix with AI'}
            </Button>
          )}
        </Stack>
      }
    >
      {heading}
      {warnings.map((one, index) => (
        <Typography key={`${String(index)} ${one.message}`} variant="body2">
          {one.message}
        </Typography>
      ))}
      {warnings.length > 0 && !fixable && (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Next step: AI may not rewrite this to meet the rule. {byHand}
        </Typography>
      )}
      {failed && (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {FIX_FAILED}
        </Typography>
      )}
    </Alert>
  );
}
